import React, { useEffect, useRef, useState } from 'react';
import useMenuStore from 'src/store/useMenuStore';
import SearchInput from '../form/input/SearchInput';
import { MenuFoldOutlined, FolderOutlined, FileOutlined, FolderOpenTwoTone } from '@ant-design/icons';
import useUiuxStore from 'src/store/useUiuxStore';
import { MenuDto } from 'src/dtos/menu/menuDto';
import { useMenuOpen } from 'src/hooks/useMenuOpen';
import { menuIcon_First } from 'src/utils/menu/menuUtil';
import { getMinorCodeList } from 'src/apis/commonCodeApi';
import { BookmarkCdList, useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { FileWithLinesIcon } from '../common';
import { getVersion } from 'src/apis/versionApi';
import { VersionDto } from 'src/dtos/version/VersionDto';
import { SysCodeDto } from 'src/types/base';
import { showWarning } from 'src/utils/toast/toastUtil';
import { resetAllStores } from 'src/utils/store/resetAllStores';

const Sidebar: React.FC = () => {
	const { setSidebarVisible, activeTab } = useUiuxStore();
	const { openMenuFromDto, openMenuFromBookmark } = useMenuOpen();
	const { 
		menuList, thirdMenuList,fourthMenuList, fifthMenuList, openedMenuMap, openedSecondMenuMap, openedFourthMenuMap, openedFifthMenuMap,  // 5차 메뉴 상태 추가
		toggleMenu, toggleSecondMenu, toggleFourthMenu, toggleFifthMenu  // 5차 메뉴 토글 추가
	} = useMenuStore();
	const { bookmarkCdList, bookMarkCd } = useCommonCodeStore();

	
	// 1차 메뉴만 필터링 (useYn='Y'인 메뉴만)
	const firstLevelMenus = menuList.filter(menu => menu.menuLevel === 1 && menu.useYn === 'Y');
	
	// 하위 메뉴를 찾는 헬퍼 함수들 (subMenu 속성에서 가져오기)
	const getSecondMenus = (parentMenu: MenuDto) => 
		parentMenu.subMenu ? parentMenu.subMenu.filter(menu => menu.useYn === 'Y') : [];
	
	const getThirdMenus = (parentMenu: MenuDto) => 
		parentMenu.subMenu ? parentMenu.subMenu.filter(menu => menu.useYn === 'Y') : [];
	
	const getFourthMenus = (parentMenu: MenuDto) => 
		parentMenu.subMenu ? parentMenu.subMenu.filter(menu => menu.useYn === 'Y') : [];
	
	const getFifthMenus = (parentMenu: MenuDto) => 
		parentMenu.subMenu ? parentMenu.subMenu.filter(menu => menu.useYn === 'Y') : [];
	

	const [programCodes, setProgramCodes] = useState<VersionDto>();

	const [search, setSearch] = useState('');
	const [searchedList, setSearchedList] = useState<MenuDto[]>([]);

	const [sidebarWidth, setSidebarWidth] = useState(200); // 초기 px
	const isResizing = useRef(false);

	useEffect(() => {
		if (search.length > 0) {
			const menuCodeRegex = /^[A-Z]{3}\d{4}_[A-Z]\d{2}$/;
			// 3차, 4차, 5차 메뉴 모두 검색
			const allSearchableMenus = [...thirdMenuList, ...fourthMenuList, ...fifthMenuList];
			const upperCaseSearch = search.toUpperCase();

			setSearchedList(allSearchableMenus.filter(value => {
				const isMenuNameMatch = value.menuNm.toUpperCase().includes(upperCaseSearch);

				//메뉴코드는 완전일치시에만 검색시키게 정규식 적용
				const isSearchTermValidMenuCode = menuCodeRegex.test(upperCaseSearch);
				const isMenuCodeMatch = isSearchTermValidMenuCode && value.menuCd.toUpperCase() === upperCaseSearch;
				
				return isMenuNameMatch || isMenuCodeMatch;
			}));
		} else {
			setSearchedList([]);
		}
	}, [search, thirdMenuList, fourthMenuList, fifthMenuList]);

	useEffect(() => {
		const fetchProgramCodes = async () => {
			try {
				const res = await getVersion();
				setProgramCodes(res.data.data);
				const fetchedVersion = res.data.data?.systemVersion;
				if (fetchedVersion) {
					const storedVersion = localStorage.getItem('systemVersion');
					if (storedVersion !== fetchedVersion) {
						localStorage.setItem('systemVersion', fetchedVersion);
						resetAllStores();
						(window.location as any).reload(true);
					}
				}else{
					showWarning('시스템 버전 정보를 확인할 수 없습니다.');
					localStorage.setItem('systemVersion', '0.0.0');
				}
			} catch (e) {
				setProgramCodes(undefined);
			}
		};
		fetchProgramCodes();
	}, []);

	const handleMouseDown = () => {
		isResizing.current = true;
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const handleMouseMove = (e: MouseEvent) => {
		if (!isResizing.current) return;
		const newWidth = e.clientX;
		if (newWidth >= 120 && newWidth <= 500) { // 최소, 최대 폭 제한
			setSidebarWidth(newWidth);
		}
	};

	const handleMouseUp = () => {
		isResizing.current = false;
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
	};

	const isSideMenuSelected = (menu: MenuDto) => {
		if(menu.devSts && menu.devSts >= 30) {
			return true;
		}
		return false;
	}

	return (
		<div style={{ width: `${sidebarWidth + 2}px`, minWidth: '122px', maxWidth: '502px', height: '100%', flexShrink: 0 }} className='h-full max-h-full flex'>
			<div className="bg-blue-lightest h-full max-h-full flex flex-col" style={{ width: `${sidebarWidth}px` }}>
				<div className='w-full flex justify-between pt-[8px] px-[8px]'>
					<div className='w-[80%]'>
						<SearchInput search={search} setSearch={setSearch} />
					</div>
					<MenuFoldOutlined className='pr-[6px] text-[20px]' onClick={() => setSidebarVisible(false)} />
				</div>

				<div className="flex-1 overflow-y-auto overflow-x-auto px-[8px]">
					{search === '' ? <ul className="space-y-2 w-full">

						<li key={bookMarkCd}>
								<div
									className={`hover:bg-blue hover:text-white p-2 rounded cursor-pointer flex justify-between items-center font-semi`}
									onClick={() => toggleMenu(bookMarkCd)}
								>
									<div className="flex items-center gap-1">
										{menuIcon_First({menuCd: '$$$', menuNm: '즐겨찾기'})}
										{'즐겨찾기'}
									</div>
									<span>{openedMenuMap[bookMarkCd] ? '-' : '+'}</span>
							</div>
							{openedMenuMap[bookMarkCd] && <div className="ml-3 space-y-1">
								{bookmarkCdList.map(bookmark => (
									<div key={bookmark.key}
									className={`p-1 rounded cursor-pointer flex justify-between items-center font-semi hover:bg-blue hover:text-white`}
									onClick={() => openMenuFromBookmark(bookmark)}
									>
										<div className="flex items-center gap-1">
											<FileWithLinesIcon className="min-w-[20px] w-[20px] h-[20px]" style={{ color: '#1D4ED8' }} />
											{bookmark.name}
										</div>
									</div>
								))
								}
								</div>
							}
						</li>
						
						{firstLevelMenus.filter(menu => menu.menuCd !== bookMarkCd).map((menu) => (
							// 1 메뉴 - 즐겨찾게 제외
							<li key={menu.menuCd}>
								<div
									className={`hover:bg-blue hover:text-white p-2 rounded cursor-pointer flex justify-between items-center font-semi`}
									onClick={() => toggleMenu(menu.menuCd)}
								>
									<div className="flex items-center gap-1">
										{menuIcon_First(menu)}
										{menu.menuNm}
									</div>
									<span>{openedMenuMap[menu.menuCd] ? '-' : '+'}</span>
								</div>
								{openedMenuMap[menu.menuCd] && <ul className="ml-3 space-y-1">
									{getSecondMenus(menu).map((secondMenu) => (
										// 2 메뉴
										<li key={secondMenu.menuCd}>
											<div
												className={`p-1 rounded cursor-pointer hover:bg-blue hover:text-white flex justify-between items-center font-semi`}
												onClick={() => toggleSecondMenu(secondMenu.menuCd)}>
												<div className="flex items-center gap-1">
													{openedSecondMenuMap[secondMenu.menuCd]
														? <FolderOpenTwoTone twoToneColor={['#1D4ED8', '#1D4ED8']} style={{ fontSize: '20px', color: '#1D4ED8' }} />
														: <FolderOutlined style={{ color: '#1D4ED8', fontSize: '20px' }} />}
													{secondMenu.menuNm}
												</div>
												<span>{openedSecondMenuMap[secondMenu.menuCd] ? '-' : '+'}</span>
											</div>
											{openedSecondMenuMap[secondMenu.menuCd] && <ul className="ml-4 space-y-1">
												{getThirdMenus(secondMenu).map((thirdMenu) => {
													const clickable = thirdMenu.devSts && thirdMenu.devSts >= 30;
													const hasSubMenu = getFourthMenus(thirdMenu).length > 0;

													// 3차 메뉴
													return <li key={thirdMenu.menuCd}>
														<div
															className={`p-1 rounded cursor-pointer flex justify-between items-center font-semi ${
																clickable ? 'hover:bg-blue hover:text-white' : 'text-gray-500'
															}`}
															onClick={() => hasSubMenu ? toggleFourthMenu(thirdMenu.menuCd) : (clickable ? openMenuFromDto(thirdMenu) : undefined)}>
															<div className="flex items-center gap-1">
																{hasSubMenu ? (
																	openedFourthMenuMap[thirdMenu.menuCd]
																		? <FolderOpenTwoTone twoToneColor={['#1D4ED8', '#1D4ED8']} style={{ fontSize: '20px', color: '#1D4ED8' }} />
																		: <FolderOutlined style={{ color: '#1D4ED8', fontSize: '20px' }} />
																) : (
																	<FileWithLinesIcon className="min-w-[20px] w-[20px] h-[20px]" style={{ color: '#1D4ED8' }} />
																)}
																{thirdMenu.menuNm}
															</div>
															{hasSubMenu && (
																<span>{openedFourthMenuMap[thirdMenu.menuCd] ? '-' : '+'}</span>
															)}
														</div>
														
														{/* 4차 메뉴 추가 */}
														{hasSubMenu && openedFourthMenuMap[thirdMenu.menuCd] && (
															<ul className="ml-5 space-y-1">
																{getFourthMenus(thirdMenu).map((fourthMenu) => {
																	const fourthClickable = fourthMenu.devSts && fourthMenu.devSts >= 30;
																	const fourthHasSubMenu = getFifthMenus(fourthMenu).length > 0;
																	
																	return <li key={fourthMenu.menuCd}>
																		<div
																			className={`p-1 rounded cursor-pointer flex justify-between items-center font-semi ${
																				fourthClickable ? 'hover:bg-blue hover:text-white' : 'text-gray-500'
																			} ${activeTab?.key === fourthMenu.menuCd ? 'ring-2 ring-inset ring-blue' : ''}`}
																			onClick={() => fourthHasSubMenu ? toggleFifthMenu(fourthMenu.menuCd) : (fourthClickable ? openMenuFromDto(fourthMenu) : undefined)}>
																			<div className="flex items-center gap-1">
																				{fourthHasSubMenu ? (
																					openedFifthMenuMap[fourthMenu.menuCd]
																						? <FolderOpenTwoTone twoToneColor={['#1D4ED8', '#1D4ED8']} style={{ fontSize: '20px', color: '#1D4ED8' }} />
																						: <FolderOutlined style={{ color: '#1D4ED8', fontSize: '20px' }} />
																				) : (
																					<FileWithLinesIcon className="min-w-[20px] w-[20px] h-[20px]" style={{ color: '#1D4ED8' }} />
																				)}
																				{fourthMenu.menuNm}
																			</div>
																			{fourthHasSubMenu && (
																				<span>{openedFifthMenuMap[fourthMenu.menuCd] ? '-' : '+'}</span>
																			)}
																		</div>
																		
																		{/* 5차 메뉴 추가 */}
																		{fourthHasSubMenu && openedFifthMenuMap[fourthMenu.menuCd] && (
																			<ul className="ml-6 space-y-1">
																				{getFifthMenus(fourthMenu).map((fifthMenu) => {
																					const fifthClickable = isSideMenuSelected(fifthMenu);
																					
																					return <li key={fifthMenu.menuCd}>
																						<div
																							className={`p-1 rounded cursor-pointer ${fifthClickable ? 'hover:bg-blue hover:text-white' : 'text-gray-500'} flex items-center gap-1 
																										${activeTab?.key === fifthMenu.menuCd ? 'ring-2 ring-inset ring-blue' : ''}`}
																							onClick={fifthClickable ? () => openMenuFromDto(fifthMenu) : undefined}>
																							<FileWithLinesIcon className="min-w-[20px] w-[20px] h-[20px]" style={{ color: '#1D4ED8' }} />
																							{fifthMenu.menuNm}
																						</div>
																					</li>
																				})}
																			</ul>
																		)}
																	</li>
																})}
															</ul>
														)}
													</li>
												})}
											</ul>}
										</li>
									))}
								</ul>}
							</li>
						))}
					</ul>
					:
					//검색값 입력했을때의 표기
					searchedList.map(thirdMenu => {
						const clickable = isSideMenuSelected(thirdMenu);
						return <div key={thirdMenu.menuCd}
							className={`w-full ps-4 text-start cursor-pointer ${clickable ? 'hover:bg-blue hover:text-white' : 'text-gray-500'}`}
							onClick={clickable ? () => openMenuFromDto(thirdMenu) : undefined}>
							{thirdMenu.menuNm}
						</div>
					})}
				</div>

				<div className="sticky bottom-0 w-full px-[8px] py-[8px] bg-[#f5f7fa] border-t border-[#e5e7eb]">
					{programCodes ? (
						<div className="text-center mb-1">
							<div className="text-[12px] font-bold text-[#333]">
								Raonzena MES <br/>
								Version {programCodes.systemVersion ? programCodes.systemVersion : '--'}
							</div>
						</div>
					) : null}
				</div>
			</div>
			<div
				className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
				onMouseDown={handleMouseDown}
			/>
		</div>
	);
};

export default Sidebar;